import { LiquidationSuspenseApplied } from '@willow/types-iso';

import { add, multiply, subtract } from './math';

interface LiquidationProceeds {
  remainingBalanceOwed: number;
  totalPaid: number;
}

interface LiquidationSuspenseBalances {
  statementSuspenseBalance: number;
  futureStatementSuspenseBalance: number;
  unappliedSuspenseBalance: number;
}

export const getLiquidationSuspenseApplied = ({
  statementSuspenseBalance,
  futureStatementSuspenseBalance,
  unappliedSuspenseBalance,
}: LiquidationSuspenseBalances): { suspenseApplied: LiquidationSuspenseApplied; totalSuspense: number } => {
  const suspenseApplied = {
    statementSuspense: Math.max(statementSuspenseBalance, 0), // clamping to avoid reducing proceed balance
    futureStatementSuspense: Math.max(futureStatementSuspenseBalance, 0),
    unappliedSuspense: Math.max(unappliedSuspenseBalance, 0),
  };
  return {
    suspenseApplied,
    totalSuspense: add(
      suspenseApplied.statementSuspense,
      suspenseApplied.futureStatementSuspense,
      suspenseApplied.unappliedSuspense,
    ),
  };
};

// A negative `remainingBalanceOwed` means the liquidation proceeds exceeded everything owed; the
// overage is parked in suspense. The gross transaction amount is the proceeds applied to balances
// (`totalPaid`) plus that suspense overage. Shared so the recorded transaction
// (`liquidationToTransactionInput`) and the activity-feed UI can't drift.
export const getLiquidationSuspenseAndAmount = ({ remainingBalanceOwed, totalPaid }: LiquidationProceeds) => {
  const suspense = remainingBalanceOwed < 0 ? multiply(remainingBalanceOwed, -1) : 0;
  return { suspense, amount: add(totalPaid, suspense) };
};

interface LiquidationSuspenseColumns extends LiquidationProceeds {
  suspenseApplied?: LiquidationSuspenseApplied;
}

export const getLiquidationSuspenseColumns = ({
  remainingBalanceOwed,
  totalPaid,
  suspenseApplied,
}: LiquidationSuspenseColumns): { suspense: number; unappliedSuspense: number; amount: number } => {
  const { suspense: overage, amount: grossAmount } = getLiquidationSuspenseAndAmount({
    remainingBalanceOwed,
    totalPaid,
  });
  const statementApplied = add(suspenseApplied?.statementSuspense ?? 0, suspenseApplied?.futureStatementSuspense ?? 0);
  const unappliedApplied = suspenseApplied?.unappliedSuspense ?? 0;
  return {
    suspense: subtract(0, statementApplied),
    unappliedSuspense: subtract(overage, unappliedApplied),
    amount: subtract(grossAmount, add(statementApplied, unappliedApplied)), // net incoming money (aka sales proceeds)
  };
};
